import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    RandevuOzet, GUN_KISA, tarihOku, tarihStr, saatStr, gunBasi, gunEkle, haftaBasi, ayniGun, aktifDurum, durumMeta,
} from './randevuOrtak';
import './randevu.css';

export type TakvimGorunum = 'gun' | 'hafta' | 'ay' | 'ekip';

export interface PersonelDoluluk {
    kullaniciID: number;
    adSoyad: string;
    randevuAlabilir: boolean;
    gunler: { gun: number; baslangicSaati: string; bitisSaati: string; molaBaslangic?: string | null; molaBitis?: string | null }[];
    blokeler: { blokeID: number; baslangic: string; bitis: string; tur: string; aciklama?: string | null }[];
}

interface Props {
    gorunum: TakvimGorunum;
    tarih: Date;
    randevular: RandevuOzet[];
    doluluk?: PersonelDoluluk[];         // gün/hafta: seçili personel(ler); ekip: kolonlar
    tekPersonelID?: number | null;       // gün/hafta görünümünde gölgelendirme için
    personelGoster?: boolean;            // olayda personel adı
    onBosTikla?: (baslangic: Date, personelID?: number) => void;
    onRandevuTikla: (id: number) => void;
    onGunTikla?: (gun: Date) => void;
}

const SATIR_PX = 24;          // 30 dakika
const SAAT_PX = SATIR_PX * 2;
const dk = (s?: string | null) => { if (!s) return null; const [h, m] = s.split(':').map(Number); return h * 60 + (m || 0); };

// Çakışan randevuları yan yana şeritlere yerleştir
function seritle(liste: RandevuOzet[]) {
    const sirali = [...liste].sort((a, b) => a.baslangic.localeCompare(b.baslangic) || b.bitis.localeCompare(a.bitis));
    const sonuc = new Map<number, { serit: number; toplam: number }>();
    let kume: RandevuOzet[] = []; let kumeSonu = '';
    let seritSonlari: string[] = [];
    const kumeyiKapat = () => { const n = seritSonlari.length; kume.forEach(r => { const x = sonuc.get(r.randevuID)!; x.toplam = n; }); kume = []; seritSonlari = []; };
    for (const r of sirali) {
        if (kume.length && r.baslangic >= kumeSonu) kumeyiKapat();
        let serit = seritSonlari.findIndex(s => s <= r.baslangic);
        if (serit < 0) { serit = seritSonlari.length; seritSonlari.push(r.bitis); } else seritSonlari[serit] = r.bitis;
        sonuc.set(r.randevuID, { serit, toplam: 1 });
        kume.push(r);
        if (r.bitis > kumeSonu || kume.length === 1) kumeSonu = r.bitis > kumeSonu ? r.bitis : kumeSonu;
    }
    if (kume.length) kumeyiKapat();
    return sonuc;
}

const RandevuTakvim: React.FC<Props> = ({ gorunum, tarih, randevular, doluluk, tekPersonelID, personelGoster, onBosTikla, onRandevuTikla, onGunTikla }) => {
    const kapRef = useRef<HTMLDivElement>(null);
    const [simdi, setSimdi] = useState(new Date());
    useEffect(() => { const z = window.setInterval(() => setSimdi(new Date()), 60000); return () => window.clearInterval(z); }, []);

    // ── Kolonlar ──
    const kolonlar = useMemo(() => {
        if (gorunum === 'gun') return [{ anahtar: tarihStr(tarih), gun: gunBasi(tarih), personelID: tekPersonelID ?? undefined, baslik: tarih.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' }), alt: '' }];
        if (gorunum === 'hafta') {
            const bas = haftaBasi(tarih);
            return Array.from({ length: 7 }, (_, i) => { const g = gunEkle(bas, i); return { anahtar: tarihStr(g), gun: g, personelID: tekPersonelID ?? undefined, baslik: `${GUN_KISA[g.getDay()]} ${g.getDate()}`, alt: g.toLocaleDateString('tr-TR', { month: 'short' }) }; });
        }
        if (gorunum === 'ekip') return (doluluk ?? []).map(p => ({ anahtar: `p${p.kullaniciID}`, gun: gunBasi(tarih), personelID: p.kullaniciID, baslik: p.adSoyad, alt: p.randevuAlabilir ? '' : 'randevu almıyor' }));
        return [];
    }, [gorunum, tarih, doluluk, tekPersonelID]);

    // ── Saat aralığı (07–21, randevular dışarı taşıyorsa genişler) ──
    const [ilkSaat, sonSaat] = useMemo(() => {
        let a = 7, b = 21;
        for (const r of randevular) {
            const s = tarihOku(r.baslangic), e = tarihOku(r.bitis);
            a = Math.min(a, s.getHours());
            b = Math.max(b, e.getHours() + (e.getMinutes() > 0 ? 1 : 0));
        }
        return [Math.max(0, a), Math.min(24, Math.max(b, a + 1))];
    }, [randevular]);

    // İlk açılışta mesai başına kaydır
    useEffect(() => {
        if (gorunum === 'ay' || !kapRef.current) return;
        const hedef = (Math.max(8, new Date().getHours() - 1) - ilkSaat) * SAAT_PX;
        kapRef.current.scrollTop = Math.max(0, hedef);
    }, [gorunum, tarih, ilkSaat]);

    if (gorunum === 'ay') {
        const ayBas = new Date(tarih.getFullYear(), tarih.getMonth(), 1);
        const bas = haftaBasi(ayBas);
        const gunler = Array.from({ length: 42 }, (_, i) => gunEkle(bas, i));
        const sonHafta = gunler.findIndex((g, i) => i % 7 === 0 && g.getMonth() !== tarih.getMonth() && g > ayBas);
        const goster = sonHafta > 0 ? gunler.slice(0, sonHafta) : gunler;
        const gunluk = new Map<string, RandevuOzet[]>();
        for (const r of randevular) { const k = tarihStr(tarihOku(r.baslangic)); gunluk.set(k, [...(gunluk.get(k) ?? []), r]); }
        return (
            <div className="rnd-ay">
                {[1, 2, 3, 4, 5, 6, 0].map(g => <div key={g} className="gun-bas">{GUN_KISA[g]}</div>)}
                {goster.map(g => {
                    const liste = (gunluk.get(tarihStr(g)) ?? []).sort((a, b) => a.baslangic.localeCompare(b.baslangic));
                    return (
                        <div key={tarihStr(g)} className={`gun${g.getMonth() !== tarih.getMonth() ? ' disi' : ''}${ayniGun(g, simdi) ? ' bugun' : ''}`}
                            onClick={() => onGunTikla?.(g)} title="Günü aç">
                            <span className="no">{g.getDate()}</span>
                            {liste.slice(0, 4).map(r => (
                                <div key={r.randevuID} className={`rnd-cip${aktifDurum(r.durum) || r.durum === 'TAMAMLANDI' ? '' : ' pasif'}`}
                                    style={{ background: `${r.renk}1a` }} title={`${saatStr(tarihOku(r.baslangic))} ${r.baslik}${personelGoster ? ` · ${r.personelAdi}` : ''}`}
                                    onClick={e => { e.stopPropagation(); onRandevuTikla(r.randevuID); }}>
                                    <span className="nokta" style={{ background: r.renk }}></span>
                                    <b>{saatStr(tarihOku(r.baslangic))}</b> {r.baslik}
                                </div>
                            ))}
                            {liste.length > 4 && <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, paddingLeft: 4 }}>+{liste.length - 4} daha</div>}
                        </div>
                    );
                })}
            </div>
        );
    }

    const saatSayisi = sonSaat - ilkSaat;
    const yukseklik = saatSayisi * SAAT_PX;
    const dakikaPx = (d: number) => ((d - ilkSaat * 60) / 30) * SATIR_PX;

    const kolonRandevulari = (k: typeof kolonlar[number]) => randevular.filter(r => {
        const s = tarihOku(r.baslangic);
        if (!ayniGun(s, k.gun)) return false;
        return gorunum !== 'ekip' || r.personelID === k.personelID;
    });

    const kapaliAlanlar = (k: typeof kolonlar[number]) => {
        const p = (doluluk ?? []).find(x => x.kullaniciID === k.personelID);
        if (!p) return [];
        const alanlar: { ust: number; yuk: number; tur: 'kapali' | 'bloke'; etiket?: string }[] = [];
        if (p.gunler.length > 0) {
            const g = p.gunler.find(x => x.gun === k.gun.getDay());
            if (!g) alanlar.push({ ust: 0, yuk: yukseklik, tur: 'kapali' });
            else {
                const b = dk(g.baslangicSaati)!, e = dk(g.bitisSaati)!;
                if (b > ilkSaat * 60) alanlar.push({ ust: 0, yuk: dakikaPx(b), tur: 'kapali' });
                if (e < sonSaat * 60) alanlar.push({ ust: dakikaPx(e), yuk: yukseklik - dakikaPx(e), tur: 'kapali' });
                const mb = dk(g.molaBaslangic), me = dk(g.molaBitis);
                if (mb != null && me != null) alanlar.push({ ust: dakikaPx(mb), yuk: dakikaPx(me) - dakikaPx(mb), tur: 'kapali', etiket: 'Mola' });
            }
        }
        const gunBasMs = k.gun.getTime(), gunSonMs = gunBasMs + 86400000;
        for (const bl of p.blokeler) {
            const s = tarihOku(bl.baslangic).getTime(), e = tarihOku(bl.bitis).getTime();
            if (e <= gunBasMs || s >= gunSonMs) continue;
            const sd = Math.max(0, (s - gunBasMs) / 60000), ed = Math.min(1440, (e - gunBasMs) / 60000);
            const ust = Math.max(0, dakikaPx(sd)), alt = Math.min(yukseklik, dakikaPx(ed));
            if (alt > ust) alanlar.push({ ust, yuk: alt - ust, tur: 'bloke', etiket: `${bl.tur === 'IZIN' ? 'İzin' : 'Bloke'}${bl.aciklama ? ': ' + bl.aciklama : ''}` });
        }
        return alanlar;
    };

    const kolonGenislik = gorunum === 'ekip' ? 'minmax(150px, 1fr)' : 'minmax(110px, 1fr)';

    return (
        <div className="rnd-izgara-kap" ref={kapRef}>
            {kolonlar.length === 0 && <div className="tkp-bos" style={{ padding: 24, textAlign: 'center' }}>Gösterilecek personel yok.</div>}
            {kolonlar.length > 0 && (
                <div className="rnd-izgara" style={{ gridTemplateColumns: `52px repeat(${kolonlar.length}, ${kolonGenislik})` }}>
                    <div className="kose"></div>
                    {kolonlar.map(k => (
                        <div key={k.anahtar} className={`kolon-bas${gorunum !== 'ekip' && ayniGun(k.gun, simdi) ? ' bugun' : ''}`}
                            style={{ cursor: gorunum === 'hafta' && onGunTikla ? 'pointer' : undefined, textTransform: gorunum === 'gun' ? 'capitalize' : undefined }}
                            onClick={() => gorunum === 'hafta' && onGunTikla?.(k.gun)} title={k.baslik}>
                            {k.baslik}{k.alt && <div className="alt">{k.alt}</div>}
                        </div>
                    ))}

                    <div className="saat-kolon">
                        {Array.from({ length: saatSayisi }, (_, i) => <div key={i} className="saat">{i === 0 ? '' : `${String(ilkSaat + i).padStart(2, '0')}:00`}</div>)}
                    </div>
                    {kolonlar.map(k => {
                        const liste = kolonRandevulari(k);
                        const seritler = seritle(liste);
                        const bugunMu = ayniGun(k.gun, simdi);
                        const simdiDk = simdi.getHours() * 60 + simdi.getMinutes();
                        return (
                            <div key={k.anahtar} className="rnd-gun-kolon" style={{ height: yukseklik }}>
                                {kapaliAlanlar(k).map((a, i) => (
                                    <div key={i} className={a.tur} style={{ top: a.ust, height: a.yuk }} title={a.etiket}>{a.tur === 'bloke' ? a.etiket : null}</div>
                                ))}
                                {Array.from({ length: saatSayisi * 2 }, (_, i) => (
                                    <div key={i} className="hucre" onClick={() => {
                                        if (!onBosTikla) return;
                                        const d = new Date(k.gun); d.setHours(ilkSaat, i * 30, 0, 0);
                                        onBosTikla(d, k.personelID);
                                    }} />
                                ))}
                                {bugunMu && simdiDk >= ilkSaat * 60 && simdiDk <= sonSaat * 60 && <div className="rnd-simdi" style={{ top: dakikaPx(simdiDk) }} />}
                                {liste.map(r => {
                                    const s = tarihOku(r.baslangic), e = tarihOku(r.bitis);
                                    const sd = s.getHours() * 60 + s.getMinutes();
                                    const ed = ayniGun(s, e) ? e.getHours() * 60 + e.getMinutes() : 24 * 60;
                                    const ust = dakikaPx(sd), yuk = Math.max(18, dakikaPx(ed) - ust - 1);
                                    const sr = seritler.get(r.randevuID) ?? { serit: 0, toplam: 1 };
                                    const gen = 100 / sr.toplam;
                                    const pasif = r.durum === 'IPTAL' || r.durum === 'ERTELENDI';
                                    const dm = durumMeta(r.durum);
                                    return (
                                        <div key={r.randevuID} className={`rnd-olay${pasif ? ' pasif' : r.durum === 'TAMAMLANDI' || r.durum === 'GELMEDI' ? ' bitti' : ''}`}
                                            style={{ top: ust, height: yuk, left: `calc(${sr.serit * gen}% + 2px)`, width: `calc(${gen}% - 4px)`, background: `${r.renk}22`, borderLeftColor: r.renk }}
                                            onClick={ev => { ev.stopPropagation(); onRandevuTikla(r.randevuID); }}
                                            title={`${saatStr(s)}–${saatStr(e)} ${r.baslik}\n${dm.ad}${r.musteriAdi ? `\n${r.musteriAdi}` : ''}${r.konum ? `\n${r.konum}` : ''}${personelGoster ? `\n${r.personelAdi}` : ''}`}>
                                            {yuk < 34 ? (
                                                <div className="b"><span className="s" style={{ marginRight: 4 }}>{saatStr(s)}</span>{r.baslik}</div>
                                            ) : (
                                                <>
                                                    <div className="s">{saatStr(s)}{r.durum !== 'PLANLANDI' && <i className={`fas ${dm.ikon}`} style={{ marginLeft: 4, color: dm.renk }}></i>}{r.musteriOnay === 'ONAYLADI' && <i className="fas fa-user-check" style={{ marginLeft: 4, color: '#0d9488' }} title="Müşteri onayladı"></i>}</div>
                                                    <div className="b">{r.baslik}</div>
                                                </>
                                            )}
                                            {yuk > 44 && (r.musteriAdi || r.konum) && <div className="k">{r.musteriAdi ?? r.konum}</div>}
                                            {yuk > 60 && personelGoster && <div className="k"><i className="fas fa-user" style={{ marginRight: 3 }}></i>{r.personelAdi}</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RandevuTakvim;
